import React, { useState, useEffect } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PurchasesServices from "../../../Services/Inventory Services/PurchasesServices";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { formatDate } from "../../../Components/FormatDate/FormatDate";
import PurchaseModal from "./PurchaseModal";
import PurchaseModalValidationSchema from "../../../validation/PurchaseValidationSchema/PurchaseModalValidationSchema";
import PurchaseTable from "./PurchaseTable";
import { removeLeadingZeros } from "../../../Components/RemoveLeadingZeros/RemoveLeadingZeros";
import NumberCommas from "../../../Components/NumberCommas/NumberCommas";
import ItemService from "../../../Services/QRCodeServices/MenuItem/ItemService";
import SupplierServices from "../../../Services/QRCodeServices/SupplierServices/SupplierServices";
import Select from "react-select";

const PurchaseEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const idValue = id || 0;

  const [formData, setFormData] = useState({
    editRecordID: idValue,
    isInEditMode: !!id,
    isChildRecordInEditMode: false,
    activeChildRecord: {
      key: "k_0",
      id: 0,
      itemId: "",
      quantity: "",
      purchaseRate: "",
      mfgDate: "",
      expDate: "",
      batchNo: "",
    },
    editChildRecordArrayIndex: 0,
    advanceSummary: {
      valueData: new Date().toISOString().split("T")[0],
      remarks: "",
      disc_Amt: 0,
      disc_Percentage: 0,
      supplierId: 0,
      purchaseItems: [],
    },
  });
  const [productsId, setProductsId] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductIDs, setSelectedProductIDs] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});
  const [discountType, setDiscountType] = useState("percent");
  const [touchedModalFields, setTouchedModalFields] = useState({});
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalErrors, setModalErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplierList, setSupplierList] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const productOptions = productsId.map((product) => ({
    value: product.id,
    label: product.title,
  }));

  const supplierOptions = supplierList?.map((supplier) => ({
    value: supplier?.id,
    label: supplier?.supplierName,
  }));

  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);

    if (selectedOption) {
      setModalErrors((prev) => ({ ...prev, itemId: undefined }));
    }

    if (!selectedOption) {
      setFormData((prevData) => ({
        ...prevData,
        activeChildRecord: {
          ...prevData.activeChildRecord,
          itemId: "",
        },
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      activeChildRecord: {
        ...prevData.activeChildRecord,
        itemId: selectedOption.value,
      },
    }));
  };

  const validationSchema = Yup.object().shape({
    valueData: Yup.date().nullable().required("Value date is required"),
    remarks: Yup.string().required("Remarks are required"),
  });

  const validateData = async () => {
    try {
      await validationSchema.validate(formData.advanceSummary, {
        abortEarly: false,
      });

      setErrors({});
      return true;
    } catch (validationErrors) {
      if (validationErrors.inner) {
        const newErrors = {};
        validationErrors.inner.forEach((error) => {
          newErrors[error.path] = `${error.message} `;
        });
        setErrors(newErrors);
      } else {
        console.error(
          "Validation errors not found or in unexpected format:",
          validationErrors
        );
      }
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedProducts = await ItemService.getList({
          pageNumber: 1,
          pageSize: 1000,
        });
        setProductsId(fetchedProducts?.data);

        if (formData.isInEditMode) {
          await getRelatedDataFromAPI();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [formData.isInEditMode, formData.editRecordID]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedProducts = await ItemService.getList({
          pageNumber: 1,
          pageSize: 1000,
        });
        setProductsId(fetchedProducts?.data);

        // Only fetch purchase details after products are loaded
        if (formData.isInEditMode && formData.editRecordID > 0) {
          const apiResponse = await PurchasesServices.view(
            formData.editRecordID
          );
          const formattedDate = formatDate(apiResponse.valueData);

          // Map the purchaseItems array correctly with formatted dates
          const purchaseItemsList = apiResponse.purchaseItems
            ? apiResponse.purchaseItems.map((detail) => ({
                ...detail,
                key: "o_" + detail.id,
                title:
                  fetchedProducts?.data.find((p) => p.id === detail.itemId)
                    ?.title || "",
                mfgDate: detail.mfgDate ? formatDate(detail.mfgDate) : "",
                expDate: detail.expDate ? formatDate(detail.expDate) : "",
              }))
            : [];
          // Set selected supplier for edit mode
          const selectedSupplierOption = supplierList.find(
            (supplier) => supplier.id === apiResponse.supplierId
          );
          if (selectedSupplierOption) {
            setSelectedSupplier({
              value: selectedSupplierOption.id,
              label: selectedSupplierOption.supplierName,
            });
          }

          setFormData((prevData) => ({
            ...prevData,
            advanceSummary: {
              ...prevData.advanceSummary,
              ...apiResponse,
              valueData: formattedDate,
              purchaseItems: purchaseItemsList,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [formData.isInEditMode, formData.editRecordID]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => {
      const newValue =
        name === "disc_Amt" || name === "disc_Percentage"
          ? removeLeadingZeros(value)
          : value;

      if (name === "disc_Amt") {
        return {
          ...prevData,
          advanceSummary: {
            ...prevData.advanceSummary,
            disc_Amt: newValue,
            disc_Percentage: 0,
          },
        };
      } else if (name === "disc_Percentage") {
        return {
          ...prevData,
          advanceSummary: {
            ...prevData.advanceSummary,
            disc_Amt: 0,
            disc_Percentage: newValue,
          },
        };
      }

      const newData = {
        ...prevData,
        advanceSummary: {
          ...prevData.advanceSummary,
          [name]: newValue,
        },
      };

      if (touchedFields[name]) {
        validationSchema
          .validateAt(name, newData.advanceSummary)
          .then(() => {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
          })
          .catch((error) => {
            setErrors((prev) => ({ ...prev, [name]: error.message }));
          });
      }

      return newData;
    });
  };

  const handleBlur = (fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateField(fieldName);
  };

  const handleModalBlur = (fieldName) => {
    setTouchedModalFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateModalField(fieldName);
  };

  const validateField = async (fieldName) => {
    try {
      await validationSchema.validateAt(fieldName, formData.advanceSummary);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: undefined,
      }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error.message,
      }));
    }
  };

  const modalValidationSchema = PurchaseModalValidationSchema;

  const validateModalField = async (fieldName) => {
    try {
      await modalValidationSchema.validateAt(
        fieldName,
        formData.activeChildRecord
      );
      setModalErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    } catch (error) {
      setModalErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handleTableInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      const newActiveChild = {
        ...prevData.activeChildRecord,
        [name]: value,
      };
      const newData = {
        ...prevData,
        activeChildRecord: newActiveChild,
      };

      if (touchedModalFields[name]) {
        modalValidationSchema
          .validateAt(name, newActiveChild)
          .then(() => {
            setModalErrors((prev) => ({ ...prev, [name]: undefined }));
          })
          .catch((error) => {
            setModalErrors((prev) => ({ ...prev, [name]: error.message }));
          });
      }

      return newData;
    });
  };

  const handleTableDetailIncrease = () => {
    setFormData((prevData) => ({
      ...prevData,
      activeChildRecord: {
        key: `k_${prevData.advanceSummary.purchaseItems.length}`,
        id: 0,
        itemId: "",
        quantity: "",
        purchaseRate: 0,
        batchNo: "", // Initialize batchNo field
      },
    }));
    setModalOpen(true);
    setSelectedProduct(null);
  };

  const handleEdit = (index) => {
    const purchaseItem = formData.advanceSummary.purchaseItems[index];

    const matchingProduct = productsId.find(
      (product) => product.id === purchaseItem.itemId
    );

    const selectedProductOption = matchingProduct
      ? {
          value: matchingProduct.id,
          label: matchingProduct.name,
        }
      : null;

    setSelectedProduct(selectedProductOption);

    setFormData((prevData) => ({
      ...prevData,
      isChildRecordInEditMode: true,
      editChildRecordArrayIndex: index,
      activeChildRecord: {
        ...purchaseItem,
      },
    }));

    setModalOpen(true);
  };

  const handleDelete = (index) => {
    const updatedPurchaseItems = formData.advanceSummary.purchaseItems.filter(
      (_, i) => i !== index
    );
    setFormData((prevData) => ({
      ...prevData,
      advanceSummary: {
        ...prevData.advanceSummary,
        purchaseItems: updatedPurchaseItems,
      },
    }));
  };

  const handleSaveSale = async () => {
    const allModalFields = ["itemId", "quantity", "purchaseRate", "batchNo"];

    setTouchedModalFields(
      allModalFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    try {
      await modalValidationSchema.validate(formData.activeChildRecord, {
        abortEarly: false,
      });

      const selectedProductTitle = selectedProduct ? selectedProduct.label : "";

      const newPurchaseItem = {
        ...formData.activeChildRecord,
        title: selectedProductTitle, // Add this line
      };

      if (formData.isChildRecordInEditMode) {
        const updatedPurchaseItems = [...formData.advanceSummary.purchaseItems];
        updatedPurchaseItems[formData.editChildRecordArrayIndex] =
          newPurchaseItem;

        setFormData((prevData) => ({
          ...prevData,
          advanceSummary: {
            ...prevData.advanceSummary,
            purchaseItems: updatedPurchaseItems,
          },
          isChildRecordInEditMode: false,
        }));
      } else {
        setFormData((prevMasterData) => ({
          ...prevMasterData,
          advanceSummary: {
            ...prevMasterData.advanceSummary,
            purchaseItems: [
              ...prevMasterData.advanceSummary.purchaseItems,
              newPurchaseItem,
            ],
          },
        }));
      }

      setModalOpen(false);
      resetModalForm();
      setModalErrors({});
    } catch (validationErrors) {
      const newErrors = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach((error) => {
          newErrors[error.path] = error.message;
        });
      }
      setModalErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const allFields = Object.keys(validationSchema.fields);
    const touchedAll = allFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: true,
      }),
      {}
    );
    setTouchedFields(touchedAll);

    const isValid = await validateData();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare the data for submission
      const activeChild = formData.activeChildRecord;

      // Clean up purchaseItems to exclude empty mfgDate and expDate
      const cleanedPurchaseItems = formData.advanceSummary.purchaseItems.map(
        (item) => ({
          ...item,
          mfgDate: item.mfgDate === "" ? null : item.mfgDate, // Convert empty string to null
          expDate: item.expDate === "" ? null : item.expDate, // Convert empty string to null
        })
      );

      // Check if activeChild has valid data and add it to the cleaned array
      if (
        activeChild.itemId > 0 &&
        activeChild.quantity.length > 0 &&
        activeChild.purchaseRate.length > 0 &&
        activeChild.batchNo.length > 0
      ) {
        const cleanedActiveChild = {
          ...activeChild,
          mfgDate: activeChild.mfgDate === "" ? null : activeChild.mfgDate, // Convert empty string to null
          expDate: activeChild.expDate === "" ? null : activeChild.expDate, // Convert empty string to null
        };
        cleanedPurchaseItems.push(cleanedActiveChild);
      }

      // Prepare the final payload with cleaned purchaseItems
      const payload = {
        ...formData.advanceSummary,
        purchaseItems: cleanedPurchaseItems,
      };

      if (formData.isInEditMode) {
        const updatedPurchase = await PurchasesServices.update(
          formData.editRecordID,
          payload
        );
        toast.success("Purchase Updated Successfully", { autoClose: 3000 });
      } else {
        const newPurchase = await PurchasesServices.create(payload);
        toast.success("Purchase Added Successfully", { autoClose: 3000 });
      }

      navigate("/purchase");
    } catch (error) {
      toast.error(error.response?.data || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateClick = (e) => {
    const dateInput = e.currentTarget.querySelector('input[type="date"]');
    if (dateInput) {
      dateInput.focus();
      dateInput.click();
      if (typeof dateInput.showPicker === "function") {
        try {
          dateInput.showPicker();
        } catch (err) {
          console.log("showPicker not supported");
        }
      }
    }
  };

  const handleSupplierChange = (selectedOption) => {
    setSelectedSupplier(selectedOption);
    setFormData((prevData) => ({
      ...prevData,
      advanceSummary: {
        ...prevData.advanceSummary,
        supplierId: selectedOption ? selectedOption.value : 0,
      },
    }));

    // Clear supplierId error if a supplier is selected
    if (selectedOption) {
      setErrors((prev) => ({ ...prev, supplierId: undefined }));
    }
  };

  const resetModalForm = () => {
    setFormData((prevData) => ({
      ...prevData,
      isChildRecordInEditMode: false,
      activeChildRecord: {
        key: `k_${prevData.advanceSummary.purchaseItems.length}`,
        id: 0,
        itemId: "",
        quantity: "",
        purchaseRate: "",
        mfgDate: "",
        expDate: "",
        batchNo: "", // Reset batchNo field
      },
    }));
    setSelectedProduct(null);
    setModalErrors({});
  };

  const handleModalClose = () => {
    resetModalForm();
    setModalOpen(false);

    setSelectedProductIDs((prevSelectedProductIDs) =>
      prevSelectedProductIDs.filter(
        (id) => id !== formData.activeChildRecord.itemId
      )
    );
  };

  useEffect(() => {
    if (formData.isInEditMode) {
      const vendorData = null;
      if (vendorData) {
        setSelectedVendorData(vendorData);
      }
    }
  }, [formData.isInEditMode]);

  const calculateSubtotal = () => {
    return formData.advanceSummary.purchaseItems.reduce((total, item) => {
      const itemTotal =
        parseFloat(item.quantity) * parseFloat(item.purchaseRate);
      return total + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === "percent") {
      const percentage =
        parseFloat(formData.advanceSummary.disc_Percentage) || 0;
      return (subtotal * percentage) / 100;
    } else {
      return parseFloat(formData.advanceSummary.disc_Amt) || 0;
    }
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();

    return subtotal - discountAmount;
  };

  useEffect(() => {
    if (formData.isInEditMode && formData.advanceSummary) {
      if (
        parseFloat(formData.advanceSummary.disc_Amt) > 0 &&
        parseFloat(formData.advanceSummary.disc_Percentage) === 0
      ) {
        setDiscountType("amount");
      } else {
        setDiscountType("percent");
      }
    }
  }, [formData.isInEditMode, formData.advanceSummary]);

  const fetchSuppliers = async () => {
    try {
      const response = await SupplierServices.getList();
      setSupplierList(response.data);
    } catch (error) {
      console.log("🚀 ~ error Failed to fetch:", error);
      toast.error(error?.response?.data || "Failed to fetch suppliers");
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Purchase Fields"
            pageTitle="Purchase"
            pageLink="/purchases"
          />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    <Row className="mb-4">
                      <Col sm={4}>
                        <Row>
                          <Label for="supplierId" sm={4}>
                            Supplier:
                          </Label>

                          <Col sm={8}>
                            <Select
                              id="supplierId"
                              options={supplierOptions}
                              value={selectedSupplier}
                              onChange={handleSupplierChange}
                              placeholder="Select a supplier"
                              isClearable
                              className={
                                touchedFields.supplierId && errors.supplierId
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {touchedFields.supplierId && errors.supplierId && (
                              <div className="invalid-feedback d-block">
                                {errors.supplierId}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Col>

                      <Col sm={4}>{/* Empty middle column */}</Col>

                      <Col sm={4}>
                        <Row>
                          <Label for="valueData" sm={6}>
                            Value Date:
                          </Label>
                          <Col sm={6}>
                            <div
                              className="date-input-wrapper position-relative"
                              onClick={handleDateClick}
                              style={{ cursor: "pointer" }}
                            >
                              <Input
                                type="date"
                                name="valueData"
                                id="valueData"
                                value={formData.advanceSummary.valueData}
                                onChange={(e) =>
                                  setFormData((prevData) => ({
                                    ...prevData,
                                    advanceSummary: {
                                      ...prevData.advanceSummary,
                                      valueData: e.target.value,
                                    },
                                  }))
                                }
                                className={`form-control ${
                                  touchedFields.valueData && errors.valueData
                                    ? "is-invalid"
                                    : ""
                                }`}
                                onBlur={() => handleBlur("valueData")}
                                style={{ cursor: "pointer" }}
                              />
                              {touchedFields.valueData && errors.valueData && (
                                <div className="invalid-feedback">
                                  {errors.valueData}
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <PurchaseTable
                      purchaseItems={formData.advanceSummary.purchaseItems}
                      handleTableDetailIncrease={handleTableDetailIncrease}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                    />

                    <Row className="mt-4">
                      <Col md={4}>
                        <Label> Remarks:</Label>

                        <Input
                          type="textarea"
                          name="remarks"
                          id="remarks"
                          value={formData.advanceSummary.remarks}
                          onChange={handleInputChange}
                          className={`form-control ${
                            touchedFields.remarks && errors.remarks
                              ? "is-invalid"
                              : ""
                          }`}
                          onBlur={() => handleBlur("remarks")}
                          rows="6"
                          placeholder="Enter remarks here..."
                        />
                        {touchedFields.remarks && errors.remarks && (
                          <div className="invalid-feedback">
                            {errors.remarks}
                          </div>
                        )}
                      </Col>
                      <Col md={4}></Col>

                      <Col md={4}>
                        <Row className="mb-2">
                          <Col md={6} className="mb-2">
                            <Input
                              type="select"
                              id="discountType"
                              name="discountType"
                              value={discountType}
                              onChange={(e) => setDiscountType(e.target.value)}
                            >
                              <option value="amount">Disc. Amount</option>
                              <option value="percent">Disc. Percent</option>
                            </Input>
                          </Col>
                          <Col md={6}>
                            {discountType === "amount" ? (
                              <div className="input-group flex-nowrap">
                                <span className="input-group-text">Rs</span>
                                <Input
                                  type="number"
                                  id="disc_Amt"
                                  value={formData.advanceSummary.disc_Amt}
                                  name="disc_Amt"
                                  onChange={handleInputChange}
                                  placeholder="Enter Amount"
                                  className="form-control"
                                  aria-label="Amount"
                                />
                              </div>
                            ) : (
                              <div className="input-group flex-nowrap">
                                <Input
                                  type="number"
                                  id="disc_Percentage"
                                  value={
                                    formData.advanceSummary.disc_Percentage
                                  }
                                  name="disc_Percentage"
                                  onChange={handleInputChange}
                                  placeholder="Enter Percentage"
                                  className="form-control"
                                  aria-label="Percentage"
                                />
                                <span className="input-group-text">%</span>
                              </div>
                            )}
                          </Col>
                        </Row>
                        <Card className="border shadow-sm">
                          <CardBody className="(py-3">
                            <Row className="mb-2">
                              <Col xs={7} className="text-muted">
                                Subtotal:
                              </Col>
                              <Col xs={5} className="text-end">
                                Rs <NumberCommas number={calculateSubtotal()} />
                              </Col>
                            </Row>

                            {discountType === "percent" &&
                              parseFloat(
                                formData.advanceSummary.disc_Percentage
                              ) > 0 && (
                                <>
                                  <Row className="mb-1">
                                    <Col xs={7} className="text-muted">
                                      Discount (%):
                                    </Col>
                                    <Col xs={5} className="text-end">
                                      {formData.advanceSummary.disc_Percentage}%
                                    </Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col xs={7} className="text-muted">
                                      Discount Amount:
                                    </Col>
                                    <Col xs={5} className="text-end">
                                      Rs{" "}
                                      <NumberCommas
                                        number={calculateDiscountAmount()}
                                      />
                                    </Col>
                                  </Row>
                                </>
                              )}

                            {(discountType === "amount" ||
                              parseFloat(
                                formData.advanceSummary.disc_Percentage
                              ) === 0) && (
                              <Row className="mb-2">
                                <Col xs={7} className="text-muted">
                                  Discount:
                                </Col>
                                <Col xs={5} className="text-end">
                                  Rs{" "}
                                  <NumberCommas
                                    number={calculateDiscountAmount()}
                                  />
                                </Col>
                              </Row>
                            )}

                            <hr className="my-2" />

                            <Row className="fw-bold">
                              <Col xs={7}>Net Amount:</Col>
                              <Col xs={5} className="text-end">
                                Rs <NumberCommas number={calculateTotal()} />
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                    <FormGroup row>
                      <div className="d-flex gap-2 justify-content-end">
                        <Button
                          type="submit"
                          className="btn btn-success"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          className="btn btn-danger"
                          onClick={() => navigate(-1)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </FormGroup>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <PurchaseModal
            isOpen={modalOpen}
            onClose={handleModalClose}
            onSave={handleSaveSale}
            formData={formData}
            selectedProduct={selectedProduct}
            productOptions={productOptions}
            handleProductChange={handleProductChange}
            handleTableInputChange={handleTableInputChange}
            touchedModalFields={touchedModalFields}
            modalErrors={modalErrors}
            handleModalBlur={handleModalBlur}
            isChildRecordInEditMode={formData.isChildRecordInEditMode}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PurchaseEditor;
